// src/services/vibeStats.js — Vibes analytics, ported from the app so the two
// screens cannot disagree.
//
// ══ THE DISCREPANCY THIS FILE EXISTS TO END ═══════════════════════════════
// The phone said "Accounts reached 3" and the browser said 6, for the same
// account, on the same day. The phone was right. The browser was doing this:
//
//     reach: a.reach + (p.reachedUsers?.length || 0)      // ← summed per post
//     views: a.views + (Number(p.viewsCount) || 0)        // ← the raw counter
//
// Three separate errors in two lines:
//
//   1. SUMMING A PER-POST LIST DOUBLE-COUNTS PEOPLE. `reachedUsers` is the
//      ledger of WHO saw one post. One person who looked at three of your posts
//      is three entries across three ledgers — and one account reached. "Reached"
//      is a count of PEOPLE, so it has to be the size of the union, not the sum
//      of the parts. That alone is most of 6 vs 3.
//   2. THE OWNER IS IN THE LEDGER. Opening your own post used to append your uid
//      (the record-time guard came later, and never cleaned up what it inherited).
//      You are not an account you reached.
//   3. `viewsCount` IS A RAW COUNTER, not an audience. It increments per view, so
//      a replay counts again, and it carries the same legacy self-views. The app
//      abandoned it: when a ledger exists the ledger wins, and `viewsCount` is a
//      fallback for posts made before ledgers existed.
//
// Every number below is derived the way `getPostAudience` and the account-stats
// effect in the app's VibesScreen derive it — deduped, owner-free, ledger-first.

import { auth } from '../config/firebase';
import { BACKEND_URL } from './socket';
import { audienceToMetrics } from './insightsView';
import { isPagedLikes, likeTotalOf } from './likeLedger';

/* ── the audience of ONE post ──────────────────────────────────────────────
   Mirrors getPostAudience(). Note views === reach whenever a ledger exists:
   the app counts a person once however many times they looked, by design
   ("a replay is not a new view"). */
export function postAudience(post) {
  const raw = Array.isArray(post?.reachedUsers) ? post.reachedUsers : [];
  const viewerUids = [...new Set(raw)].filter((uid) => !!uid && uid !== post?.userId);
  /* 👁️ the server's three numbers (viewRoutes.js) — the phone's getPostAudience exactly:
       reach        accounts it was shown to, each once (the old first-500 list is a floor)
       views        accounts that actually looked (on screen ≥1.5 s, or played)
       impressions  times it was on a screen */
  const reach = Math.max(viewerUids.length, Number(post?.reachCount) || 0);
  const views = Number(post?.viewsCount) || 0;
  return { viewerUids, views, reach, impressions: Math.max(Number(post?.impressionsCount) || 0, views, reach) };
}

/** The owner's own like never counts toward their own engagement. */
export function likesOf(post) {
  /* ❤️ a paged post's array is only its first 500 likers — the counter is the total */
  if (isPagedLikes(post)) return likeTotalOf(post);
  return Array.isArray(post?.likes)
    ? post.likes.filter((uid) => !!uid && uid !== post.userId).length
    : (Number(post?.likesCount) || 0);
}

export function commentsOf(post) {
  if (Number(post?.commentsCount) > 0) return Number(post.commentsCount);
  return Array.isArray(post?.comments) ? post.comments.length : 0;
}

/** ((likes + comments) / people reached) × 100, to one decimal, never above 100 — the app's formula. */
export function engagementRate(post) {
  const { reach } = postAudience(post);
  if (!reach) return '0.0';
  return Math.min(100, ((likesOf(post) + commentsOf(post)) / reach) * 100).toFixed(1);
}

const isVideoPost = (p) => p?.mediaType === 'video'
  || !!p?.videoUrl
  || (p?.media || []).some((m) => m?.type === 'video');

/**
 * Account-level totals, exactly as the app's analytics effect computes them.
 *   videoViews  Σ audience views over VIDEO posts
 *   impressions Σ audience views over everything else
 *   reach       the SIZE OF THE UNION of viewers across all posts
 */
export function accountStats(myPosts) {
  const all = new Set();
  const vid = new Set();
  const img = new Set();
  let videoViews = 0;
  let impressions = 0;
  let reports = 0;

  for (const p of myPosts || []) {
    const a = postAudience(p);
    if (isVideoPost(p)) {
      videoViews += a.views;
      a.viewerUids.forEach((u) => { vid.add(u); all.add(u); });
    } else {
      impressions += a.views;
      a.viewerUids.forEach((u) => { img.add(u); all.add(u); });
    }
    reports += Number(p?.reportCount) || (Array.isArray(p?.reports) ? p.reports.length : 0);
  }

  return {
    posts: (myPosts || []).length,
    videoViews,
    impressions,
    reach: all.size,          // 🟥 union, never a sum — see the header
    videoViewers: [...vid],
    imageViewers: [...img],
    allViewers: [...all],
    reports,
  };
}

/* ══ WHERE VIEWERS ARE, AND WHO THEY ARE ═══════════════════════════════════
   🟥 THIS USED TO BE WORKED OUT IN THE BROWSER: 100 sampled viewers' user
      documents, countries from phone dial codes, percentages of only the people
      it could place. The server now describes every person once, when they
      first reach the post — country from their connection, else an
      internationally written number, else Unknown (insightsRoutes.js) — and
      answers only the post's owner. */

export const flagOf = (iso) => String(iso || '').toUpperCase()
  .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

export function localizedCountry(iso, fallback, lang) {
  try {
    const dn = typeof Intl !== 'undefined' && Intl.DisplayNames
      ? new Intl.DisplayNames([lang || 'en'], { type: 'region' })
      : null;
    const v = dn?.of?.(iso);
    if (v && v !== iso) return v;
  } catch { /* fall through */ }
  return fallback;
}

/** 👁️ What was on screen / looked at (services/viewTracker) → the server, which keeps the only tally. */
export async function sendViewEvents(events) {
  const evs = (events || []).filter((e) => e && e.postId && (e.t === 'i' || e.t === 'v')).slice(0, 50);
  if (!evs.length) return true;
  const u = auth.currentUser;
  if (!u || u.isAnonymous) return true;          // a guest is not an audience the server counts
  try {
    const res = await fetch(`${BACKEND_URL}/api/vibes/views`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
      body: JSON.stringify({ events: evs }),
      keepalive: true,
    });
    return res.ok || (res.status >= 400 && res.status < 500);
  } catch { return false; }
}

/** The server's numbers and audience for one post (postId) or the whole account (no postId). */
export async function fetchInsights(postId = null, opts = {}) {
  const u = auth.currentUser;
  if (!u) return null;
  try {
    const res = await fetch(`${BACKEND_URL}/api/vibes/insights${postId ? '' : '/account'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
      /* 📈 the dashboard's period and this browser's clock offset (insightsRoutes.dashboard) */
      body: JSON.stringify(postId ? { postId } : { days: opts.days || 28, tzOffsetMin: -new Date().getTimezoneOffset() }),
    });
    if (!res.ok) return null;
    const j = await res.json();
    return j && j.ok ? j : null;
  } catch { return null; }
}

/** The server's audience → what the insights pane draws (services/insightsView, the phone's own maths). */
export function audienceForScreen(audience, lang = 'en') {
  const m = audienceToMetrics(audience, (iso) => localizedCountry(iso, iso, lang), 'Unknown');
  return {
    ...m,
    countries: m.countries.map((c) => ({ ...c, flag: c.iso ? flagOf(c.iso) : (c.unknown ? '❔' : '🌐') })),
  };
}
